import DeletePoint from '@assets/images/icons/delete-point.svg?react'
import {
  PROJECT_COLOR_MENU_ID,
  ProjectColorMenu,
} from '@components/ArtBoard/ArtboardToolbar/ProjectColorMenu'
import type { ArrowStyleType } from '@octopus-do/octopus-project-core'
import { Toolbar } from '@octopus-do/octopus-storybook'
import { Rename, Trash } from '@octopus-do/octopus-storybook/icons'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import { forwardRef, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useClickAway, useRafLoop } from 'react-use'

import type { AppState } from '@/types'
import { createSwitch, isInsidePopover } from '@/utils'
import { ARROW_TYPE_MENU_ID, ArrowTypeMenu } from './components'
import { useArrowActions } from './hooks'
import css from './styles.module.css'
import { getArrowLabel, isInsideArrowLabel } from './utils'

const DIVISION = 2

// The plate hangs 53px under its container (styles.module.css), plus a gap to the label.
const TOOLBAR_OVER_LABEL = 59

// The toolbar's place in the layers: over the canvas, under the header, the panels
// and the footer, and cut off at the artboard's edge.
const ARTBOARD_ID = 'artboard'

// Подпись пункта, который возвращает стрелке цвет её источника. Одно слово —
// чтобы пункт не растягивал меню шире сетки цветов (Karl, 18.09.2026).
const INHERIT_LABEL = 'Source'

interface PlateProps {
  colorId: string
  labelSelected: boolean
  onClick: (keys: string[]) => void
  pointId: string
  sourceColor: string
  type: ArrowStyleType
}

// A portal moves the toolbar in the DOM only: React events still bubble through
// the artboard behind it.
const stopPropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation()
}

const Plate = forwardRef<HTMLDivElement, PlateProps>(
  ({ colorId, labelSelected, onClick, pointId, sourceColor, type }, ref) => (
    <div
      ref={ref}
      className={css.container}
      data-arrow-toolbar
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
    >
      <div className={css.plate}>
        <Toolbar.Root onClick={onClick}>
          <ArrowTypeMenu type={type} />
          <ProjectColorMenu
            colorId={colorId}
            inheritHex={sourceColor}
            inheritLabel={INHERIT_LABEL}
          />
          <Toolbar.Button icon={Rename} id="label" tooltip="Label" />
          <Toolbar.Divider />
          {pointId && (
            <Toolbar.Button
              icon={DeletePoint}
              id="delete_point"
              tooltip="Delete anchor"
              variant="danger"
            />
          )}
          <Toolbar.Button
            icon={Trash}
            id="delete"
            tooltip={labelSelected ? 'Delete label' : 'Delete arrow'}
            variant="danger"
          />
        </Toolbar.Root>
      </div>
    </div>
  )
)

interface Props {
  // Тип обещает строку, но у стрелок, сохранённых до OCT-5222, поля нет вовсе:
  // в рантайме сюда приходит undefined, и это то же самое, что «как у источника».
  colorId?: string
  id: string
  pointId?: string
  // Цвет источника: им стрелка покрашена, пока своего цвета нет.
  sourceColor: string
  type: ArrowStyleType
  // What the toolbar stands at. A selector, not a ref: the arrows are remounted on
  // every redraw, and the toolbar has to outlive that.
  anchorSelector: string
  ignoreClickAwaySelector?: string
}

export const ArrowActionsToolbar: React.FC<Props> = ({
  colorId = '',
  id,
  pointId = '',
  sourceColor,
  type,
  anchorSelector,
  ignoreClickAwaySelector = '',
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const {
    labelSelected,
    onChangeColor,
    onChangeType,
    onDeletePoint,
    onDelete,
    onEditLabel,
  } = useArrowActions(id)

  const isArrowSelected = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.arrow.selectedArrowId !== null
  )

  useClickAway(ref, ({ target }) => {
    if (
      !isArrowSelected ||
      isInsidePopover(target) ||
      isInsideArrowLabel(id, target)
    ) {
      return
    }

    if (
      ignoreClickAwaySelector &&
      target instanceof Element &&
      target.closest(ignoreClickAwaySelector)
    ) {
      return
    }

    dispatch({
      type: CanvasActionTypes.RESET_SELECTED_ARROW_ID,
    })
  })

  const switcher = createSwitch()
    // the menu reports ARROW_TYPE_OPTIONS ids, which are ArrowStyleType values
    .case([ARROW_TYPE_MENU_ID], (next) => onChangeType(next as ArrowStyleType))
    // an empty id means the arrow goes back to its source's colour
    .case([PROJECT_COLOR_MENU_ID], onChangeColor)
    .case(['label'], onEditLabel)
    .case(['delete_point'], onDeletePoint)
    .case(['delete'], onDelete)

  const onClick = (keys: string[]) => switcher.run(keys)

  const positionToolbar = () => {
    const toolbar = ref.current
    const artboard = document.getElementById(ARTBOARD_ID)

    if (!toolbar || !artboard) {
      return
    }

    // The toolbar of a point stays at its point; the one of the arrow goes over the
    // label, which it would otherwise cover and keep from the second click.
    const label = pointId ? null : getArrowLabel(id)
    const anchor = label ?? document.querySelector(anchorSelector)

    // Between a redraw and the next paint the arrow may have no anchor at all.
    toolbar.style.visibility = anchor ? '' : 'hidden'

    if (!anchor) {
      return
    }

    const toolbarRect = toolbar.getBoundingClientRect()
    const anchorRect = anchor.getBoundingClientRect()
    const artboardRect = artboard.getBoundingClientRect()

    const top =
      anchorRect.top -
      artboardRect.top -
      (label ? TOOLBAR_OVER_LABEL : toolbarRect.height)
    const left =
      anchorRect.left -
      artboardRect.left +
      anchorRect.width / DIVISION -
      toolbarRect.width / DIVISION

    toolbar.style.top = `${top}px`
    toolbar.style.left = `${left}px`
  }

  // Each frame while open: nothing reports that xarrows has moved its label.
  useRafLoop(positionToolbar)

  const artboard = document.getElementById(ARTBOARD_ID)

  if (!artboard) {
    return null
  }

  return ReactDOM.createPortal(
    <Plate
      ref={ref}
      colorId={colorId}
      labelSelected={labelSelected}
      pointId={pointId}
      sourceColor={sourceColor}
      type={type}
      onClick={onClick}
    />,
    artboard
  )
}
